import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';

import { PLAYBACK_RATE_MAX, PLAYBACK_RATE_MIN } from 'constants/params';
import { ClipIdParsed } from 'components/app/editor/_mediator/editor.slice';
import { ErrorRateValue } from 'components/app/storyboard/_mediator/sb.slice';
import { getClipLabel } from './_app.helper';

enum ErrorType {
  AccessType,
}

export type ParsedReduxError = {
  message: string;
  stack?: string;
  extra?: string;
  isInternalServerError?: boolean;
};

export const Error = {
  reduxErrorHandler: (error: unknown): ParsedReduxError => {
    if (!error) return { message: 'Unknown error' };
    if (typeof error === 'string') return { message: error };
    if (error instanceof AxiosError) {
      const message =
        (error.response?.data?.errors && Object.values(error.response.data.errors).flat().join()) ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any).error ||
        JSON.stringify(error);
      if (error.response?.data.type === ErrorType.AccessType) toast.info(message, { theme: 'colored' });
      else toast.error(message);
      return {
        message,
        stack: error?.stack,
        extra: JSON.stringify(serializeError(error)),
        isInternalServerError: !!error?.response?.data?.error,
      };
    }
    return { message: 'Unhandled error format', extra: JSON.stringify(error) };
  },
  tooManyClips: (clipsLen: number, available: number) => {
    toast.error(
      `ERROR: You have attempted to add more clips (${clipsLen}) than available beat marker slots (${available}). Please add additional audio beat markers, or remove some clips.`
    );
  },
  clipExists: (clip: ClipIdParsed) => {
    toast.error(`ERROR: Clip already exists: ${getClipLabel(clip)}`);
  },
  tooSmallRegion: (val?: 'first' | 'last') => {
    switch (val) {
      case 'first':
        toast.error(`ERROR: the first beat cell duration is too short`);
        break;
      case 'last':
        toast.error(`ERROR: the last beat cell duration is too short`);
        break;
      default:
        toast.error(`ERROR: Beat cell duration is too short, try to increase it a little more`);
        break;
    }
  },
  notEnoughCells: () => {
    toast.error(`ERROR: There are not enough empty cells`);
  },
  keepSelectedNotEnoughCells: () => {
    toast.error(
      `ERROR: Can't keep selected markers — not enough empty storyboard cells to rearrange clips. Clear or unlink some clips and try again.`
    );
  },
  keepSelectedBrokenLinks: () => {
    toast.error(
      `ERROR: Can't keep selected markers — linked clips can't be rearranged into contiguous empty cells. Unlink or move clips and try again.`
    );
  },
  invalidClipPlaybackRate: (value: ErrorRateValue) => {
    if (value === 'max') return `Invalid Clip playback rate: greater than allowed (${PLAYBACK_RATE_MAX})`;
    else return `Invalid Clip playback rate: lower than allowed (${PLAYBACK_RATE_MIN})`;
  },
  renderWithInvalidPlaybackRate: () => {
    return `Some clips have an invalid playback rate`;
  },
  renderWithEmptyClipsMap: () => {
    return `Can't create final cut for story without story board clips`;
  },
  invalidStoryLength: (opt1: string, opt2: string, metric: string) => {
    return `Invalid ${opt1} + ${opt2} ${metric}:  greater than story length`;
  },
  sbIsNotInitialized: () => {
    return `Storyboard is not initialized`;
  },
  linkingNotPossible: () => {
    toast.error(`Linking is not possible`);
  },
  handleSbLinks: () => {
    toast.error(
      `These clips can't be linked because there insufficient empty contiguous cells on the storyboard timeline`
    );
  },
  addClip: {
    invalidStartTime: () => {
      toast.error(`Clip start time cannot be equal to the video track duration`);
    },
    invalidEndTime: () => {
      toast.error(`Clip end time cannot be equal to 0`);
    },
    noStoryboardCell: () => {
      toast.error(`Couldn't add to the storyboard — no available cell was found`);
    },
  },
};
